const encoder = new TextEncoder();

const encodeUtf8 = (value) => encoder.encode(String(value ?? ''));

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
};

const toDosTimeDate = (date) => {
  const d = date instanceof Date ? date : new Date();
  const year = Math.max(1980, d.getFullYear());
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = Math.floor(d.getSeconds() / 2);
  const dosTime = (hours << 11) | (minutes << 5) | seconds;
  const dosDate = ((year - 1980) << 9) | (month << 5) | day;
  return { dosTime, dosDate };
};

const u16 = (value) => {
  const buf = new Uint8Array(2);
  const view = new DataView(buf.buffer);
  view.setUint16(0, value & 0xFFFF, true);
  return buf;
};

const u32 = (value) => {
  const buf = new Uint8Array(4);
  const view = new DataView(buf.buffer);
  view.setUint32(0, value >>> 0, true);
  return buf;
};

const concatSize = (chunks) => chunks.reduce((sum, chunk) => sum + chunk.length, 0);

const concatBytes = (chunks) => {
  const total = concatSize(chunks);
  const out = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    out.set(chunk, offset);
    offset += chunk.length;
  });
  return out;
};

const zipFileEntry = ({ name, dataBytes, mod, crc, localHeaderOffset }) => {
  const nameBytes = encodeUtf8(name);
  const { dosTime, dosDate } = toDosTimeDate(mod);
  const size = dataBytes.length;

  const localHeader = concatBytes([
    u32(0x04034b50),
    u16(20),
    u16(0),
    u16(0),
    u16(dosTime),
    u16(dosDate),
    u32(crc),
    u32(size),
    u32(size),
    u16(nameBytes.length),
    u16(0),
    nameBytes,
  ]);

  const centralHeader = concatBytes([
    u32(0x02014b50),
    u16(20),
    u16(20),
    u16(0),
    u16(0),
    u16(dosTime),
    u16(dosDate),
    u32(crc),
    u32(size),
    u32(size),
    u16(nameBytes.length),
    u16(0),
    u16(0),
    u16(0),
    u16(0),
    u32(0),
    u32(localHeaderOffset),
    nameBytes,
  ]);

  return { localHeader, centralHeader, nameBytes, size };
};

export const createZipBlob = (files) => {
  const fileList = Array.isArray(files) ? files : [];
  const chunks = [];
  const centralChunks = [];
  let offset = 0;
  const mod = new Date();

  fileList.forEach((file) => {
    const name = String(file?.name ?? '');
    if (!name) return;
    const dataBytes = file?.dataBytes instanceof Uint8Array ? file.dataBytes : encodeUtf8(file?.data ?? '');
    const crc = crc32(dataBytes);
    const localHeaderOffset = offset;
    const entry = zipFileEntry({ name, dataBytes, mod, crc, localHeaderOffset });
    chunks.push(entry.localHeader, dataBytes);
    offset += entry.localHeader.length + dataBytes.length;
    centralChunks.push(entry.centralHeader);
  });

  const centralStart = offset;
  const centralDirectory = concatBytes(centralChunks);
  chunks.push(centralDirectory);
  offset += centralDirectory.length;
  const centralSize = offset - centralStart;

  const endRecord = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(centralChunks.length),
    u16(centralChunks.length),
    u32(centralSize),
    u32(centralStart),
    u16(0),
  ]);
  chunks.push(endRecord);

  return new Blob(chunks, { type: 'application/zip' });
};

const buildDocxXml = ({ title, paragraphs }) => {
  const safeTitle = escapeXml(title ?? 'Export');
  const bodyParagraphs = [];
  bodyParagraphs.push(
    `<w:p><w:r><w:t xml:space="preserve">${safeTitle}</w:t></w:r></w:p>`,
  );
  (paragraphs ?? []).forEach((line) => {
    const text = escapeXml(line);
    bodyParagraphs.push(`<w:p><w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`);
  });
  bodyParagraphs.push('<w:p><w:r><w:t xml:space="preserve"> </w:t></w:r></w:p>');
  bodyParagraphs.push('<w:sectPr><w:pgSz w:w="12240" w:h="15840"/></w:sectPr>');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">\n` +
    `<w:body>\n${bodyParagraphs.join('\n')}\n</w:body>\n</w:document>\n`;
};

export const buildDocxBlob = ({ title, paragraphs } = {}) => {
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">\n` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>\n` +
    `<Default Extension="xml" ContentType="application/xml"/>\n` +
    `<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>\n` +
    `</Types>\n`;

  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>\n` +
    `</Relationships>\n`;

  const documentXml = buildDocxXml({ title, paragraphs });

  const zip = createZipBlob([
    { name: '[Content_Types].xml', data: contentTypes },
    { name: '_rels/.rels', data: rels },
    { name: 'word/document.xml', data: documentXml },
  ]);

  return new Blob([zip], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

const columnLetters = (index) => {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

const buildXlsxSheetXml = ({ rows }) => {
  const lines = [];
  lines.push('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>');
  lines.push('<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">');
  lines.push('<sheetData>');
  (rows ?? []).forEach((row, rowIndex) => {
    const r = rowIndex + 1;
    lines.push(`<row r="${r}">`);
    (row ?? []).forEach((cellValue, colIndex) => {
      const ref = `${columnLetters(colIndex)}${r}`;
      const text = escapeXml(cellValue);
      lines.push(`<c r="${ref}" t="inlineStr"><is><t>${text}</t></is></c>`);
    });
    lines.push('</row>');
  });
  lines.push('</sheetData>');
  lines.push('</worksheet>');
  return lines.join('\n');
};

export const buildXlsxBlob = ({ sheetName = 'Sheet1', rows } = {}) => {
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">\n` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>\n` +
    `<Default Extension="xml" ContentType="application/xml"/>\n` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>\n` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>\n` +
    `</Types>\n`;

  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>\n` +
    `</Relationships>\n`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">\n` +
    `<sheets>\n` +
    `<sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>\n` +
    `</sheets>\n` +
    `</workbook>\n`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>\n` +
    `</Relationships>\n`;

  const sheetXml = buildXlsxSheetXml({ rows });

  const zip = createZipBlob([
    { name: '[Content_Types].xml', data: contentTypes },
    { name: '_rels/.rels', data: rels },
    { name: 'xl/workbook.xml', data: workbookXml },
    { name: 'xl/_rels/workbook.xml.rels', data: workbookRels },
    { name: 'xl/worksheets/sheet1.xml', data: sheetXml },
  ]);

  return new Blob([zip], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

